import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Field, bLabel } from "../scouting/parts";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { Bug, MapPin, RefreshCw, X, Info, FlaskConical, Loader2 } from "lucide-react";
import WwxForecast from "./WwxForecast";

const LVL = {
  HIGH: "bg-red-600 text-white", MODERATE: "bg-amber-500 text-white", LOW: "bg-[#7C9673] text-white",
};
const lvlBadge = (level, index) => (
  <span className={`px-2 py-1 text-xs font-bold ${LVL[level] || "bg-stone-200 text-stone-500"}`}>
    {index != null ? `${index} ` : ""}{level || "—"}
  </span>
);

export default function PestDashboard() {
  const { user } = useAuth();
  const isManager = ["admin", "supervisor"].includes(user.role);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [locFor, setLocFor] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  const load = async () => {
    const { data } = await api.get("/pest/dashboard");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    try { await api.post("/pest/refresh"); await load(); } finally { setBusy(false); }
  };

  if (!data) return <div className="p-8 font-bold uppercase text-sm">Loading pest &amp; disease data…</div>;
  const rows = data.vineyards.filter((v) => !filter || v.pm?.level === filter);

  return (
    <div className="space-y-5" data-testid="pest-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Pest &amp; Disease</h1>
          <p className="text-stone-500 text-sm mt-1">UC IPM powdery mildew risk index · real hourly weather · scouting truth</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="planner-btn" onClick={() => window.location.assign("/pest/planner")} className="h-12 px-4 bg-[#7C9673] text-white font-bold uppercase text-xs flex items-center gap-2">Weekly Spray Plan</button>
          <button data-testid="spray-orders-btn" onClick={() => window.location.assign("/pest/orders")} className="h-12 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2">Spray Orders</button>
          {isManager && (
            <button data-testid="validation-btn" onClick={() => setShowValidation(true)} className="h-12 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><FlaskConical size={15} /> Validation</button>
          )}
          <button data-testid="pest-refresh-btn" onClick={refresh} disabled={busy} className="h-12 px-4 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2 disabled:opacity-40">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} {busy ? "Updating…" : "Update Models"}
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        {["", "HIGH", "MODERATE", "LOW"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`h-10 px-4 text-xs font-bold uppercase border ${filter === f ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white border-stone-300"}`}>
            {f || "All Risk"}
          </button>
        ))}
      </div>

      {isManager && <WwxForecast />}

      <div className="bg-white border border-stone-200 divide-y divide-stone-100">
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-bold uppercase text-stone-500">
          <div className="col-span-3">Vineyard</div><div className="col-span-2">Powdery Mildew</div>
          <div className="col-span-2">Forecast</div><div className="col-span-2">Phenology / Scouting</div>
          <div className="col-span-1">Confidence</div><div className="col-span-2">Action</div>
        </div>
        {rows.map((v) => (
          <div key={v.ranch_number} data-testid={`pest-row-${v.ranch_number}`} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 items-center">
            <div className="col-span-2 md:col-span-3">
              <div className="font-bold">{v.name} <span className="text-stone-400 font-normal text-xs">· {v.ranch_number}</span></div>
              <div className="text-xs text-stone-500">{[v.client_name, `${v.blocks.length} blocks`, `${v.acres} ac`].filter(Boolean).join(" · ")}</div>
            </div>
            {!v.configured ? (
              <div className="col-span-2 md:col-span-9 flex items-center gap-3">
                <span className="text-xs text-stone-400 font-semibold">No weather source configured</span>
                {isManager && (
                  <button data-testid={`set-location-${v.ranch_number}`} onClick={() => setLocFor(v)} className="h-9 px-3 text-xs font-bold uppercase bg-[#1F3D34] text-white flex items-center gap-1"><MapPin size={13} /> Set Location</button>
                )}
              </div>
            ) : (
              <>
                <div className="md:col-span-2">
                  {lvlBadge(v.pm.level, v.pm.current_index)}
                  {v.pm.yesterday != null && <div className="text-[10px] text-stone-500 mt-1">Yesterday {v.pm.yesterday} ({v.pm.delta >= 0 ? "+" : ""}{v.pm.delta})</div>}
                </div>
                <div className="md:col-span-2 flex gap-1 flex-wrap">
                  {(v.pm.forecast || []).slice(0, 3).map((f) => (
                    <span key={f.date} title={f.date} className={`px-1.5 py-0.5 text-[10px] font-bold ${LVL[f.level]}`}>{f.index}</span>
                  ))}
                  {(v.pm.forecast || []).length === 0 && <span className="text-[10px] text-stone-400">—</span>}
                </div>
                <div className="md:col-span-2 text-xs">
                  {v.phenology ? <div>{v.phenology.stage} <span className="text-stone-400">({bLabel(v.phenology.block)})</span></div> : <div className="text-stone-400">No phenology</div>}
                  {v.scouting_mildew
                    ? <div className="text-red-700 font-semibold">Scout: {v.scouting_mildew.severity} mildew {v.scouting_mildew.location ? `@ ${v.scouting_mildew.location}` : ""}</div>
                    : <div className="text-stone-400">Scout: no mildew observed</div>}
                  <div className="text-stone-500">Botrytis: <b>{v.botrytis?.level}</b>{v.botrytis?.estimated ? " (est.)" : ""}
                    {v.botrytis?.wwx_adjusted && <span className="ml-1 text-[9px] font-bold uppercase px-1.5 py-0.5 bg-sky-100 text-sky-800 border border-sky-300" title={v.botrytis?.reason} data-testid={`wwx-bump-${v.ranch_number}`}>↑ Wx outlook</span>}
                    {" "}· OLR: <b>{v.olr?.dd != null ? `${v.olr.dd} DD` : v.olr?.status || "—"}</b></div>
                  {v.wwx_pm_note && <div className="text-[10px] text-sky-700 font-semibold" data-testid={`wwx-pm-note-${v.ranch_number}`}>{v.wwx_pm_note}</div>}
                </div>
                <div className="md:col-span-1 text-[10px] font-bold uppercase" title={v.weather.note}>
                  <span className={v.weather.confidence === "HIGH" ? "text-[#1F3D34]" : v.weather.confidence === "MODERATE" ? "text-amber-600" : "text-red-600"}>{v.weather.confidence}</span>
                  <div className="text-stone-400 font-normal">{v.weather.data_completeness}% data</div>
                </div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase px-2 py-1 bg-stone-100">{v.action}</span>
                  <button data-testid={`pest-why-${v.ranch_number}`} onClick={() => setDetail(v)} className="h-8 px-2 text-[10px] font-bold uppercase border border-[#1F3D34] text-[#1F3D34]">Why?</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="text-xs text-stone-500 bg-white border border-stone-200 p-3 flex items-start gap-2">
        <Info size={14} className="mt-0.5 shrink-0" />
        <div><b>{data.model.name}</b> — {data.model.source}. <a className="underline" href={data.model.source_url} target="_blank" rel="noreferrer">Authoritative source</a>. {data.model.model_version}. Status: <b className="uppercase">{data.model.status || "validation"}</b>, last verified {data.model.last_verified}. Decision support only — this system never authorizes an application; scouting and the pesticide label remain controlling.</div>
      </div>

      {locFor && <LocationModal v={locFor} onClose={() => setLocFor(null)} onSaved={async () => { setLocFor(null); await refresh(); }} />}
      {detail && <DetailDrawer v={detail} onClose={() => setDetail(null)} />}
      {showValidation && <ValidationModal onClose={() => setShowValidation(false)} />}
    </div>
  );
}

function LocationModal({ v, onClose, onSaved }) {
  const [address, setAddress] = useState("");
  const [ranchAddress, setRanchAddress] = useState("");
  const [useCoords, setUseCoords] = useState(false);
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/ranches").then(({ data }) => {
      const r = data.find((x) => x.ranch_number === v.ranch_number && x.address);
      if (r) {
        const composed = [r.address, r.city, [r.state, r.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
        setRanchAddress(composed);
        setAddress(composed);
      }
    });
  }, [v.ranch_number]);
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = useCoords
        ? { ranch_number: v.ranch_number, latitude: Number(lat), longitude: Number(lon) }
        : { ranch_number: v.ranch_number, address };
      await api.post("/pest/sources", body);
      await onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not save location");
    } finally { setBusy(false); }
  };
  const ready = useCoords ? lat && lon : address.trim();
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
      <div className="bg-white border border-stone-200 w-full max-w-md p-6 space-y-4" data-testid="location-modal">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Weather Source — {v.name}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <p className="text-xs text-stone-500">Public model-grid weather (Open-Meteo) at the vineyard location. Private stations and CIMIS can be attached later.</p>
        {!useCoords ? (
          <>
            <Field label="Vineyard Address">
              <input data-testid="address-input" value={address} onChange={(e) => setAddress(e.target.value)}
                placeholder="1234 Silverado Trail, Napa, CA" className="w-full h-12 px-3 border border-stone-300" />
            </Field>
            {ranchAddress && address !== ranchAddress && (
              <button type="button" data-testid="use-ranch-address-btn" onClick={() => setAddress(ranchAddress)}
                className="text-xs font-bold uppercase text-[#1F3D34] underline">Use vineyard address on file: {ranchAddress}</button>
            )}
            {ranchAddress && address === ranchAddress && (
              <div className="text-[11px] text-stone-500">Using the address on file for this vineyard. Edit it above if the blocks are elsewhere.</div>
            )}
            {!ranchAddress && <div className="text-[11px] text-stone-500">No address on file for this vineyard — type one above, or add it under Ranches.</div>}
          </>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude"><input data-testid="lat-input" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="38.297" className="w-full h-12 px-3 border border-stone-300" /></Field>
            <Field label="Longitude"><input data-testid="lon-input" value={lon} onChange={(e) => setLon(e.target.value)} placeholder="-122.286" className="w-full h-12 px-3 border border-stone-300" /></Field>
          </div>
        )}
        <button type="button" data-testid="coords-toggle" onClick={() => setUseCoords(!useCoords)} className="text-xs font-bold uppercase text-stone-500 underline">
          {useCoords ? "Enter an address instead" : "Enter coordinates instead"}
        </button>
        {err && <div className="bg-red-50 border border-red-400 text-red-800 p-2.5 text-xs font-semibold" data-testid="location-error">{err}</div>}
        <button data-testid="save-location-btn" onClick={save} disabled={busy || !ready} className="w-full h-12 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40">
          {busy ? "Locating + Running Model…" : "Save & Run Model"}
        </button>
      </div>
    </div>
  );
}

function DetailDrawer({ v, onClose }) {
  const [block, setBlock] = useState(null);
  useEffect(() => {
    api.get(`/pest/block/${v.blocks[0].id}`).then(({ data }) => setBlock(data));
  }, [v]);
  const chart = block ? [
    ...block.history.map((h) => ({ date: h.date.slice(5), observed: h.index })),
    ...block.forecast.map((f) => ({ date: f.date.slice(5), forecast: f.index })),
  ] : [];
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white border border-stone-200 w-full max-w-3xl p-6 space-y-4 max-h-[92vh] overflow-y-auto" data-testid="pest-detail">
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>{v.name} — Powdery Mildew</h3>
          <button onClick={onClose} data-testid="pest-detail-close"><X size={20} /></button>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {lvlBadge(v.pm.level, v.pm.current_index)}
          <span className="text-xs text-stone-500">as of {v.pm.current_date} · phase: {v.pm.phase}</span>
        </div>
        <div className="bg-[#7C9673]/10 border border-[#7C9673]/40 p-3 text-sm" data-testid="pest-why-panel">
          <div className="font-bold uppercase text-xs mb-1">Why did the index change?</div>
          <div>{v.pm.reason || "No observed model days yet."}</div>
          <div className="text-xs text-stone-500 mt-2">
            Model: {v.pm.current_index != null ? `${v.pm.current_index} ${v.pm.level}` : "—"} · Scouting: {v.scouting_mildew ? `${v.scouting_mildew.severity} mildew observed (${v.scouting_mildew.date})` : "no visible mildew reported"} · Phenology: {v.phenology?.stage || "—"}. Weather models predict risk; scouting records what is observed — both inform the decision.
          </div>
        </div>
        {chart.length > 0 && (
          <div className="h-64" data-testid="pest-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <ReferenceLine y={60} stroke="#DC2626" strokeDasharray="4 4" label={{ value: "HIGH", fontSize: 9, fill: "#DC2626" }} />
                <ReferenceLine y={40} stroke="#F59E0B" strokeDasharray="4 4" />
                <Line dataKey="observed" name="Observed" stroke="#1F3D34" strokeWidth={2.5} dot={{ r: 2.5 }} connectNulls={false} />
                <Line dataKey="forecast" name="Forecast (projected)" stroke="#B8860B" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 2.5 }} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {block && (
          <>
            <div className="text-xs text-stone-500">Weather: {block.source?.station_name} · {block.source?.data_completeness}% completeness · last update {(block.source?.last_update || "").slice(0, 16).replace("T", " ")}. {"Leaf wetness not measured."}</div>
            {block.scouting.length > 0 && (
              <div className="bg-white border border-stone-200 p-3 text-sm">
                <div className="font-bold uppercase text-xs mb-1">Recent Scouting (field truth)</div>
                {block.scouting.slice(0, 5).map((s, i) => (
                  <div key={i} className="flex justify-between py-0.5 border-b border-stone-100 last:border-0">
                    <span>{s.date} · {s.phenology || "—"}</span>
                    <span className="text-xs">{s.pests.length ? s.pests.map((p) => `${p.name} (${p.severity})`).join(", ") : "no pest findings"}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[11px] text-stone-500 border-t border-stone-100 pt-2">
              <b>About this model:</b> {block.model.name}. Source: {block.model.source}. <a className="underline" href={block.model.source_url} target="_blank" rel="noreferrer">{block.model.source_url}</a> · {block.model.model_version} · Last verified {block.model.last_verified}. {block.model.notes}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ValidationModal({ onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      const { data: sample } = await api.get("/pest/validate/sample");
      const { data: run } = await api.post("/pest/validate", { days: sample.days });
      setRows(run.results.map((r, i) => ({ ...r, expected: sample.expected_indices[i] })));
    })();
  }, []);
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white border border-stone-200 w-full max-w-3xl p-6 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="validation-modal">
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Model Validation — Powdery Mildew</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <p className="text-xs text-stone-500">VineOps result vs expected UC IPM behavior on a known edge-case dataset (initiation, reset, +20, 100 cap, ≥95°F deduction, −10 decay, 0 floor). 12 automated regression tests also lock this math in CI.</p>
        {!rows ? <div className="text-sm font-bold uppercase text-stone-400 p-4">Running…</div> : (
          <table className="w-full text-sm">
            <thead className="bg-[#1F3D34] text-white text-xs uppercase">
              <tr>{["Date", "Fav. Hours", "≥95°F", "VineOps", "Expected", "Match"].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.date} className="border-b border-stone-100">
                  <td className="p-2 font-mono">{r.date}</td>
                  <td className="p-2">{r.run_hours}h</td>
                  <td className="p-2">{r.hot ? "Yes" : "—"}</td>
                  <td className="p-2 font-bold">{r.index}</td>
                  <td className="p-2">{r.expected}</td>
                  <td className={`p-2 font-bold ${r.index === r.expected ? "text-[#1F3D34]" : "text-red-600"}`} data-testid={`val-${r.date}`}>{r.index === r.expected ? "✓" : "✗"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
